<template>
    <v-dialog
        v-model="dialog"
        fullscreen
    >
        <v-card>
            <v-toolbar
                dark
                color="primary"
            >
                <v-toolbar-title>Edit Conditional Message</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-toolbar-items>
                    <v-btn
                        dark
                        text
                        @click="onClickDiscardChanges"
                    >
                        Cancel
                    </v-btn>

                    <v-btn
                        dark
                        text
                        @click="onClickSave"
                    >
                        Save
                    </v-btn>
                </v-toolbar-items>
            </v-toolbar>

            <v-card-text
                class="pt-4"
                v-if="profiles != null"
            >
                <v-text-field
                    label="Label"
                    v-model="condition.label"
                    autocomplete="off"
                />

                <p>Not seeing the options you expect?  You can refresh your meta data by <a v-on:click="onClickUpdateMetaData">clicking here</a></p>

                <v-alert
                    border="left"
                    colored-border
                    color="primary"
                    elevation="2"
                    type="info"
                    dismissible
                >
                    <b>Warning:</b>  If you make changes to your metadata (for example, changing the name of a location or reservation type) may cause previously created conditional message to stop working or otherwise malfunction.<br/><br/>Any time that you change meta data, you should review and test your conditional messages to make sure they continue to work properly.
                </v-alert>

                <v-alert
                    border="left"
                    colored-border
                    color="primary"
                    elevation="2"
                    type="info"
                    dismissible
                    v-if="showDeepSyncAlert"
                >
                    The conditions <span class="font-weight-medium">Club Type</span>, <span class="font-weight-medium">Acquisition Source</span>, and <span class="font-weight-medium">Sales Associate</span> are new, and are based only events received since 2024-09-09. If there are options missing that you expect to see, you must run a deep (re)sync of your integration. To run a deep sync:
                    <ol>
                        <li>Go to Organization Settings</li>
                        <li>Go to Integration Settings</li>
                        <li>Click on the integration you want to sync</li>
                        <li>Click the button in the lower left of the dialog: "Synchronize with {integration name}"</li>
                    </ol>
                    This sync will likely take awhile to complete (minutes to hours, depending on the integration).
                </v-alert>
            </v-card-text>

            <v-card-text
                v-for="(settings, index) in profiles_by_settings_id"
                :key="'settings-'+index"
            >
                <h6 class="font-weight-bold text-subtitle-1">{{ settings.settings_label }}</h6>
                
                <v-select
                    v-for="(profile, index) in settings.profiles"
                    :key="'profile-'+index"
                    :label="profile.label"
                    :items="profile.values"
                    item-text="value_label"
                    item-value="value_id"
                    :value="findSelectionsForConditionType(profile.type)"
                    v-on:change="onSetConditionSelections(profile.type, $event)"
                    return-object
                    multiple
                ></v-select>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
const _ = require('lodash');

export default {

    data() {
        return {
            dialog: false,
            is_valid: true,

            organization_id: null,
            message_pattern_context: null,
            condition: null,
            profiles: null,
            integration_summaries: [],
        }
    },

    methods: {

        showDialog(condition, condition_profiles, integration_crm_summaries)
        {
            this.condition = _.cloneDeep(condition);
            this.profiles = _.cloneDeep(condition_profiles);
            this.integration_summaries = _.cloneDeep(integration_crm_summaries);

            this.organization_id = this.$route.params.organization_id;
            this.message_pattern_context = this.$route.params.template_context;

            this.dialog = true;
            this.$router.dialog_guard = this.dialogGuard;
        },

        closeDialog()
        {
            this.dialog = false;
            this.$router.dialog_guard = null;
        },

        dialogGuard(to, from, next)
        {
            if ( this.dialog == false)
            {
                // slipup somehow
                this.$router.dialog_guard = null;
                return;
            }
        
            // The dialog is showing...
            if ( this.is_valid )
            {
                // data is valid, so save 
                this.save();
                this.dialog = false;
                next(false);
                return;
            }
        
            this.$rc.snackbar('You must either correct the errors or discard your changes.',1500);
            this.dialog = true;
            next(false);
        },

        onClickSave()
        {
            this.save();
            this.closeDialog();
        },

        onClickDiscardChanges()
        {
            this.closeDialog();
        },

        save()
        {
            // console.log('save > condition');
            // console.log(this.condition);
            this.$emit('save',this.condition);
        },

        onClickUpdateMetaData()
        {
            this.$rc.showSpinner();
            
            this.$rc.rest(
                'PUT: /api/v1/auto-messages/conditions/update-conditional-values',
            
                {
                    'organization_id': this.organization_id,
                    'message_pattern_context': this.message_pattern_context,
                },
            
                (data) => {
                    this.$rc.hideSpinner();

                    this.profiles = data.results;
                },
            
                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error','Unable to update meta data');
                },
            );
        },

        onSetConditionSelections(type, selections)
        {
            // console.log('onSetConditionSelections > selections');
            // console.log(selections);

            let new_constraint = {
                'type' : type,
                'selections' : selections,
            };

            // console.log('onSetConditionSelections > new_constraint');
            // console.log(new_constraint);

            for ( let i = 0; i < this.condition.constraints.length; i++ )
            {
                let cur = this.condition.constraints[i];
                if ( cur.type == type )
                {
                    if ( selections.length == 0 )
                    {
                        this.condition.constraints.splice(i,1); // remove the constraint... no values selected...
                    }
                    else
                    {
                        this.condition.constraints[i] = new_constraint;
                    }
                    
                    this.$forceUpdate();
                    return;
                }
            }

            // Do not add an empty conditional...
            if ( selections.length == 0 )
            {
                return;
            }

            this.condition.constraints.push(new_constraint);
        },

        findSelectionsForConditionType(type)
        {
            const constraint = this.condition.constraints.find(constraint => constraint.type === type);
            return constraint?.selections || [];
        },

        figureLabelForSettingsId(settings_id)
        {
            const integration_summary = this.integration_summaries.find(summary => summary.settingsId === settings_id);

            // Hackery to make the enums line up
            const settings_content_type = integration_summary.contentType.toLowerCase().replace('_', '-');
            const integration_name = this.$rc.enumValues('integration_name').find(type => type.value === settings_content_type);

            return integration_summary?.integrationLabel
                || integration_summary?.brandLabel
                || integration_name?.text
                || '(Unknown)';
        }
    },
 
    computed: {

        profiles_by_settings_id()
        {
            if (! this.profiles) return [];
            
            let profiles_by_settings_id = {};

            this.profiles.forEach(profile =>
            {
                const content_type = profile.type;

                profile.values.forEach(value =>
                {
                    const settings_id = value.settings_id;

                    if (! profiles_by_settings_id[settings_id])
                    {
                        profiles_by_settings_id[settings_id] = {
                            settings_id: settings_id,
                            settings_label: this.figureLabelForSettingsId(settings_id),
                            profiles: []
                        };
                    }

                    // Only copy each profile type once
                    if (! profiles_by_settings_id[settings_id].profiles.some(p => p.type === content_type))
                    {
                        // Filter values to only include those with the same settings_id
                        const filtered_profile = {
                            ...profile,
                            values: profile.values.filter(val => val.settings_id === settings_id)
                        };

                        profiles_by_settings_id[settings_id].profiles.push(filtered_profile);
                    }
                });
            });

            let profiles_by_settings_id_list = Object.values(profiles_by_settings_id);

            profiles_by_settings_id_list.sort((left, right) => 
            {
                const cmp = left.settings_label.localeCompare(right.settings_label)
                return cmp || left.settings_id.localeCompare(right.settings_id);
            });
            
            return profiles_by_settings_id_list;

            // TODO Show selectors / settings group for options in selected but not in profile (e.g. 'lubrication-tasting--calistoga-')
        },

        showDeepSyncAlert()
        {
            return this.message_pattern_context.startsWith('wine-club-') && 
                   new Date() < new Date('2024-10-09'); // By this point, all deep syncs should have run naturally
        },

    }
}
</script>

<style lang="scss" scoped>
</style>